// P11 dashboard and reports (RPT-C-02): owner-only reads (ADR-126). One server definition per figure (REP-05), so the
// dashboard and the reports give the same totals for the same scope.
import { Refusal } from './auth';
import { supabase } from './supabase';

export const REPORTS = ['reports'] as const;
export type Period = 'today' | 'week' | 'month';
export type Basis = 'sale' | 'payment';

const n = (v: unknown) => Number(v ?? 0);

export interface Dashboard {
  from: string;
  to: string;
  basis: Basis;
  cash: number;
  sales: number;
  collected: number;
  unpaid: number;
  alerts: {
    low_stock: number;
    overdue: number;
    overdue_amount: number;
    pre_orders: number;
    late_orders: number;
    lots: number;
    lot_pieces: number;
    ledger: number;
  };
  series: { day: string; sales: number; collected: number; result: number }[];
  by_truck: { truck: string; sales: number; margin: number }[];
  best: { product: string; name: string; pieces: number; sales: number }[];
  moves: { received: number; loaded: number; sold: number; returned: number; written_off: number };
  profit: { net_sales: number; cogs: number; gross: number; expenses: number; shrinkage: number; result: number };
  value: { stock: number; damaged: number; debt: number; capital: number };
  ageing: { due: number; up_to_30: number; over_30: number };
  cash_by: { place: 'garage' | 'bank' | 'round'; truck: string | null; balance: number }[];
}

async function call<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn as never, args as never);
  if (error) throw new Refusal(/^[A-Z_]+$/.test(error.message) ? error.message : 'SERVER_ERROR');
  return data as T;
}

export async function loadDashboard(period: Period, basis: Basis): Promise<Dashboard> {
  const d = await call<Dashboard>('dashboard', { p_period: period, p_basis: basis });
  const num = <K extends string>(o: Record<K, unknown>) =>
    Object.fromEntries(Object.entries(o).map(([k, v]) => [k, n(v)])) as Record<K, number>;
  return {
    ...d,
    cash: n(d.cash),
    sales: n(d.sales),
    collected: n(d.collected),
    unpaid: n(d.unpaid),
    alerts: num(d.alerts),
    series: d.series.map((s) => ({ day: s.day, sales: n(s.sales), collected: n(s.collected), result: n(s.result) })),
    by_truck: d.by_truck.map((x) => ({ truck: x.truck, sales: n(x.sales), margin: n(x.margin) })),
    best: d.best.map((x) => ({ ...x, pieces: n(x.pieces), sales: n(x.sales) })),
    moves: num(d.moves),
    profit: num(d.profit),
    value: num(d.value),
    ageing: num(d.ageing),
    cash_by: d.cash_by.map((x) => ({ ...x, balance: n(x.balance) })),
  };
}

export interface ReportFilters {
  from: string;
  to: string;
  basis: Basis;
  truck: string | null;
  seller: string | null;
  product: string | null;
  customer: string | null;
}
export interface SalesRow {
  id: string;
  name: string;
  pieces: number;
  sales: number;
  net: number;
  cost: number;
  margin: number;
}
export interface DebtRow {
  customer_id: string;
  name: string;
  due: number;
  up_to_30: number;
  over_30: number;
  total: number;
}
export interface RoundRow {
  round_id: string;
  number: string;
  day: string;
  truck: string;
  seller: string | null;
  net: number;
  cost: number;
  margin: number;
  expenses: number;
  count_difference: number;
  variance: number | null;
  status: 'open' | 'selling_ended' | 'closed';
}
export interface ExpenseRow {
  category_id: string;
  category: string;
  fixed: boolean;
  n: number;
  total: number;
}
const nums = <T extends object>(rows: T[], keys: (keyof T)[]): T[] =>
  rows.map((r) => ({ ...r, ...Object.fromEntries(keys.map((k) => [k, r[k] === null ? null : n(r[k])])) }));

export async function loadSalesReport(f: ReportFilters, group: 'product' | 'seller' | 'customer'): Promise<SalesRow[]> {
  const rows = await call<SalesRow[]>('report_sales', {
    p_from: f.from,
    p_to: f.to,
    p_basis: f.basis,
    p_group: group,
    p_truck: f.truck,
    p_seller: f.seller,
    p_product: f.product,
    p_customer: f.customer,
  });
  return nums(rows, ['pieces', 'sales', 'net', 'cost', 'margin']);
}
export async function loadDebtReport(f: ReportFilters): Promise<DebtRow[]> {
  return nums(await call<DebtRow[]>('report_debt', { p_seller: f.seller, p_customer: f.customer }), [
    'due',
    'up_to_30',
    'over_30',
    'total',
  ]);
}
export async function loadRoundsReport(f: ReportFilters): Promise<RoundRow[]> {
  return nums(
    await call<RoundRow[]>('report_rounds', { p_from: f.from, p_to: f.to, p_truck: f.truck, p_seller: f.seller }),
    ['net', 'cost', 'margin', 'expenses', 'count_difference', 'variance'],
  );
}
export async function loadExpensesReport(f: ReportFilters): Promise<ExpenseRow[]> {
  return nums(await call<ExpenseRow[]>('report_expenses', { p_from: f.from, p_to: f.to, p_truck: f.truck }), [
    'n',
    'total',
  ]);
}
